import React, { useRef, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';

import CalendarIcon from '../../SVG-components/calendar';
import WifiTetheringIcon from '../../SVG-components/wifi_tethering';
import ArrowForwardIcon from '../../SVG-components/arrow_forward';

import TimelineDialog from './TimelineDialog';
import TimelineRange from "./TimeLineRange/TimelineRange";
import Drawer, {DrawerManager} from '../../../BemComponents/Drawer';
import {iFullStoreState, TimelineRangeType} from '../../../shared/interfaces';
import {Actions as TimelineActions} from '../../../stores/reducers/timeline-reducers';
import {useParams} from 'react-router-dom';

import './styles.scss';

enum TimelineTabs {
    timeline, live
}

function Timeline (props) {
    const dispatch = useDispatch();
    const {deviceId} = useParams<{ deviceId: string }>();

    const [timelineTab, updateTimelineTab] = useState<TimelineTabs>(TimelineTabs.live);
    const [openedSettings, setOpenedSettings] = useState(false);

    const isLive = useSelector<iFullStoreState, boolean>((s) => s.timeline.isLive);

    const startDate = useSelector<iFullStoreState, moment.Moment | undefined>(
        (s) => s.timeline.range?.startDate,
        (l, r) => l?.unix() === r?.unix(),
    );

    const endDate = useSelector<iFullStoreState, moment.Moment | undefined>(
        (s) => s.timeline.range?.endDate,
        (l, r) => l?.unix() === r?.unix(),
    );

    const rangeType = useSelector<iFullStoreState, TimelineRangeType>(s => s.timeline.rangeType)

    const drawerRef = useRef<DrawerManager>();
    const setupDrawerConfig = (callBack: () => DrawerManager) => drawerRef.current = callBack();

    const handleClickOutside = () => {
        if (openedSettings) {
            const drawer = drawerRef.current;

            drawer?.({close: true});
            setOpenedSettings(false);
            isLive && updateTimelineTab(TimelineTabs.live);
        }
    };

    const hideCalendar = () => {
        const drawer = drawerRef.current;

        drawer?.({close: true});
        setOpenedSettings(false);
        updateTimelineTab(TimelineTabs.live);
    };

    const setLiveTab = () => {
        openDrawer();

        if (timelineTab === TimelineTabs.live) return;

        updateTimelineTab(TimelineTabs.live);
        setOpenedSettings(false);

        const drawer = drawerRef.current;

        drawer?.({close: true});
        dispatch(TimelineActions.GO_LIVE());
    };

    const setTimelineTab = () => {
        if (timelineTab === TimelineTabs.timeline) return;

        updateTimelineTab(TimelineTabs.timeline);

        if (!startDate || !endDate) {
            openDrawer();
        }
    };

    const openDrawer = () => {
        const drawer = drawerRef.current;

        drawer?.();
        setOpenedSettings(true);
    };

    return (
      <>
        <div
          id="timeline-overlay"
          className={classNames('timeline-overlay', {
            live: timelineTab === TimelineTabs.live,
            'opened-settings': openedSettings,
          })}
        >
          <div className="timeline-container">
            {/* Timeline */}
            <div
              className={classNames('timeline-item', {
                active: timelineTab === TimelineTabs.timeline,
              })}
              onClick={setTimelineTab}
            >
              <CalendarIcon />
              <span>Timeline</span>
            </div>

            {/* Timeline indicator */}
            <div
              className={classNames('live-indicator', {
                timeline: timelineTab === TimelineTabs.timeline,
              })}
            />

            <div className="divider" />

            {/* Live */}
            <div
              className={classNames('timeline-item', {
                active: timelineTab === TimelineTabs.live,
              })}
              onClick={setLiveTab}
            >
              <WifiTetheringIcon />
              <span>Live</span>
            </div>

            {/* Live indicator */}
            <div
              className={classNames('live-indicator', {
                live: timelineTab === TimelineTabs.live,
              })}
            />
          </div>

          {/* Date range */}
          {startDate && endDate && (
            <div className="date-range">
              <TimelineRange start={startDate} end={endDate} />
              <span className="change-period-toggle" onClick={openDrawer}>
                <ArrowForwardIcon />
              </span>
            </div>
          )}

          <Drawer
            containerId="timeline-overlay"
            setupConfig={setupDrawerConfig}
            anchor="top"
            clickOutside={handleClickOutside}
          >
            <TimelineDialog
              drawerRef={drawerRef}
              deviceId={deviceId}
              live={!!timelineTab}
              hideCalendarCallback={hideCalendar}
              setOpenedSettings={setOpenedSettings}
              rangeType={rangeType}
              startDate={startDate}
              endDate={endDate}
            />
          </Drawer>
        </div>
      </>
    );
}

export default Timeline;